import { useLoadPasscodeListMembers } from '../../hooks/use-load-passcode-list-members';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectAssociatedEvents,
  selectAssociatedEventsDetails,
  selectPasscodeList,
} from '@/store/slices/passcode-lists/selectors';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { NavLink, useParams } from 'react-router-dom';
import {
  fetchPasscodeListAssociatedEvents,
  fetchPasscodeListAssociatedEventsDetails,
} from '@/store/slices/passcode-lists';
import { DownloadCSVStringValues, PASSCODE_LIST_TYPE } from '@/types/registration.types';
import { useCallback, useEffect, useState } from 'react';
import OptionalComponent from '@ui/optional-component/optional-component';
import PasscodeListMemberList from '../passcode-lists/passcode-list-member-table';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import PasscodeListAttachedProjects from '../passcode-lists/passcode-list-attached-projects';
import styles from './passcode-lists.module.css';
import classNames from 'classnames';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import AudiencesAddButton from '../../controls/audiences-add-button';
import SearchInput from '@ui/search-input/search-input';
import ManageRegistrantsModal from '../registrants/modals/add-registrants-modal';
import { useSaveRegistrantList } from '../../hooks/use-save-registrant-list';
import { HandleDownloadCSV } from '@/connection/audiences/passcode-lists';
import toastTrigger from '@ui/toast/toast-trigger';
import { downloadCsv, getExportDate } from '../../utils';

const AudiencesList = () => {
  const channel = useAppSelector(getActiveChannel);
  const associatedEvents = useAppSelector(selectAssociatedEvents);
  const associatedEventsDetails = useAppSelector(selectAssociatedEventsDetails);
  const passcodeList = useAppSelector(selectPasscodeList);

  const [showAddMembersModal, setShowAddMembersModal] = useState(false);

  const { projectUuid, language, passcodeListId } = useParams();

  const { reloadMembers } = useLoadPasscodeListMembers(
    channel || 0,
    Number(passcodeListId) || 0,
    undefined,
    undefined,
    undefined,
    false
  );

  const handleSaveComplete = () => {
    reloadMembers();
  };

  const {
    saveEmailsOnlyList,
    savePasscodesOnlyList,
    saveEmailsAndPasscodesList,
    saveMagicLinksList,
    saveMagicLinksRegistrationList,
    saving,
  } = useSaveRegistrantList();

  const dispatch = useAppDispatch();

  const getSaveHandler = useCallback(() => {
    if (!passcodeList || passcodeList.type == null) return saveMagicLinksRegistrationList;

    const listType = passcodeList.type as PASSCODE_LIST_TYPE;

    switch (listType) {
      case PASSCODE_LIST_TYPE.EMAILS_ONLY:
        return saveEmailsOnlyList;
      case PASSCODE_LIST_TYPE.PASSCODES_ONLY:
        return savePasscodesOnlyList;
      case PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES:
        return saveEmailsAndPasscodesList;
      case PASSCODE_LIST_TYPE.MAGIC_LINKS:
        return saveMagicLinksList;
      case PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION:
        return saveMagicLinksRegistrationList;
      default:
        return saveMagicLinksRegistrationList;
    }
  }, [
    passcodeList,
    saveEmailsAndPasscodesList,
    saveEmailsOnlyList,
    saveMagicLinksList,
    saveMagicLinksRegistrationList,
    savePasscodesOnlyList,
  ]);

  const handlePasscodeListDownload = async () => {
    try {
      if (passcodeList) {
        const response = await HandleDownloadCSV(
          passcodeList,
          DownloadCSVStringValues.NORMAL,
          undefined,
          null,
          passcodeList.dma_id
        );

        if (!response.ok) {
          toastTrigger(
            <div className="S1">
              <div>There was an error downloading the information.</div>
            </div>,
            {
              type: 'error',
              autoClose: 10000,
            }
          );
          return;
        }

        downloadCsv(response, `${passcodeList.name ? passcodeList.name : 'passcode list export'}-${getExportDate()}`);
      }
    } catch (e) {
      console.error('Error downloading magic links:', e);
      toastTrigger(
        <div className="S1">
          <div>Download failed</div>
          <div>Your audience list failed to generate</div>
        </div>,
        {
          type: 'error',
          autoClose: 10000,
        }
      );
    }
  };

  useEffect(() => {
    if (passcodeList?.passcode_list && channel) {
      dispatch(fetchPasscodeListAssociatedEvents({ passcodeListId: passcodeList.passcode_list, channel }));
    }
  }, [dispatch, passcodeList, channel]);

  useEffect(() => {
    if (associatedEvents && channel) {
      dispatch(fetchPasscodeListAssociatedEventsDetails({ eventList: associatedEvents, channel }));
    }
  }, [dispatch, associatedEvents, channel]);

  return (
    <>
      <Container className="flex-column gap-6">
        <Container className="flex ai-center">
          <NavLink
            className={classNames(styles['passcode-list-back-button'], 'flex ai-center padding-r-8')}
            to={`/project/edit/${projectUuid}/${language}/audiences/lists`}
          >
            <Icon name={ICONS.ARROW_LEFT} size={20} />
          </NavLink>
          <Text tag="h2">{passcodeList?.name ? passcodeList?.name : 'Audience'}</Text>
        </Container>
        <Container className="flex ai-center padding-b-6" style={{ borderBottom: '1px solid var(--gray-600)' }}>
          <PasscodeListAttachedProjects
            displayAssociatedEvents
            passcodeListAssociatedEvents={associatedEventsDetails}
            passcodeListDownload={handlePasscodeListDownload}
          />
          <Container className="flex ai-center gap-6" style={{ marginLeft: 'auto' }}>
            <SearchInput expandOnFocus width="300px" trackInParams debounceTime={500} isClearable isBorderless />
            <AudiencesAddButton onAddClick={() => setShowAddMembersModal(true)} />
          </Container>
        </Container>
      </Container>
      <OptionalComponent display={!!passcodeList}>
        {passcodeList ? <PasscodeListMemberList passcodeList={passcodeList} reloadMembers={reloadMembers} /> : <></>}
      </OptionalComponent>

      <ManageRegistrantsModal
        open={showAddMembersModal}
        setOpen={setShowAddMembersModal}
        onSave={getSaveHandler()}
        saving={saving}
        passcodeList={passcodeList || undefined}
        onSaveComplete={handleSaveComplete}
      />
    </>
  );
};

export default AudiencesList;
